import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';

import { APP_INSIGHTS_INSTRUMENTATION_KEY } from '@digital-dealer/environments';

import IAppInsightsCustomProperties from '../types/IAppInsightsCustomProperties';

import ignoredErrorPatterns from './ignoredErrorPatterns';

class Logger {
  private appInsights: ApplicationInsights | null = null;

  constructor() {
    if (this.appInsights || !APP_INSIGHTS_INSTRUMENTATION_KEY) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: APP_INSIGHTS_INSTRUMENTATION_KEY,
      },
    });

    this.appInsights.loadAppInsights();

    this.appInsights.addTelemetryInitializer(this.convertNotCriticalErrorToWarning);
  }

  private convertNotCriticalErrorToWarning = (pack: ITelemetryItem): boolean => {
    const { baseType, data, baseData } = pack;
    const message: string | undefined = baseData?.message || data?.message || baseData?.exceptions?.[0]?.message;

    if (
      baseType === 'ExceptionData'
      && message
      && ignoredErrorPatterns.some(
        (pattern: RegExp): boolean => pattern.test(message),
      )
    ) {
      this.trackWarning(message, {
        catcher: 'Logger.convertNotCriticalErrorToWarning',
      });

      return false;
    }

    return true;
  };

  private trackValue = (value: any, properties: IAppInsightsCustomProperties, severityLevel: SeverityLevel): void => {
    if (this.appInsights) {
      properties.route = window.location.href;
      properties.parentUrl = window.xprops.parentUrl;
      properties.referrer = window.xprops.referrer;

      if (severityLevel === SeverityLevel.Critical || severityLevel === SeverityLevel.Error) {
        this.appInsights.trackException({
          properties,
          severityLevel,
          error: value,
        });

        return;
      }

      this.appInsights.trackTrace({
        properties,
        severityLevel,
        message: value,
      });

      return;
    }

    switch (severityLevel) {
      case SeverityLevel.Critical:
      case SeverityLevel.Error:
        console.error(value);
        break;

      case SeverityLevel.Warning:
        console.warn(value);
        break;

      default:
        console.info(value);
        break;
    }
  };

  public trackError = (error: Error, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(error, properties, SeverityLevel.Error);
  };

  public trackCriticalError = (error: Error, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(error, properties, SeverityLevel.Critical);
  };

  public trackInfo = (message: string, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(message, properties, SeverityLevel.Information);
  };

  public trackWarning = (message: string, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(message, properties, SeverityLevel.Warning);
  };
}

export default new Logger();
